import { useCallback } from 'react'
import type { EventTypeRow } from '@/db/types'
import { labelColorOn } from '@/design/contrast'
import { REVEAL_WIDTH, useSwipeReveal } from './useSwipeReveal'

interface TypeRowProps {
  type: EventTypeRow
  onEdit: (type: EventTypeRow) => void
  onMove: (id: string, direction: -1 | 1) => void
  onTogglePin: (type: EventTypeRow) => void
  onArchive: (type: EventTypeRow) => void
  onDelete: (type: EventTypeRow) => void
  canMoveUp: boolean
  canMoveDown: boolean
}

/**
 * One event type in the manage list.
 *
 * Tap edits. Swiping left reveals Archive and Delete, the iOS convention, so
 * the two destructive actions are one gesture away without occupying the row
 * permanently -- there is no room for six controls on a 390px phone.
 *
 * The swipe tray is an accelerator, not the only route. It is hidden from
 * assistive technology while closed -- announcing "Archive, Delete" for every
 * row would make the list unusable to read -- so both actions also live in the
 * editor that tapping the row opens. Nothing here is reachable by touch alone.
 *
 * Reordering uses buttons rather than drag-and-drop. A drag handle inside a
 * vertical scroller on a touch screen needs a long-press to disambiguate, and
 * that same long-press is how you would reach a context menu; the two fight
 * each other. Buttons are unambiguous, work with a keyboard, and this is a
 * screen you visit rarely.
 */
export function TypeRow({
  type,
  onEdit,
  onMove,
  onTogglePin,
  onArchive,
  onDelete,
  canMoveUp,
  canMoveDown,
}: TypeRowProps) {
  const swipe = useSwipeReveal()

  const handleClick = useCallback(() => {
    // A swipe ends with a click on iOS. Without this, letting go after
    // revealing the actions would immediately open the editor over them.
    if (swipe.revealed || swipe.swiping()) return
    onEdit(type)
  }, [swipe, onEdit, type])

  return (
    <div className="mg-row-wrap" ref={swipe.ref}>
      <div className="mg-row-actions" style={{ width: `${REVEAL_WIDTH}px` }} aria-hidden={!swipe.revealed}>
        <button
          className="mg-row-action"
          tabIndex={swipe.revealed ? 0 : -1}
          onClick={() => {
            swipe.reset()
            onArchive(type)
          }}
        >
          {type.isArchived === 1 ? 'Restore' : 'Archive'}
        </button>
        <button
          className="mg-row-action mg-row-action--danger"
          tabIndex={swipe.revealed ? 0 : -1}
          onClick={() => {
            swipe.reset()
            onDelete(type)
          }}
        >
          Delete
        </button>
      </div>

      <div
        className={`mg-row ${type.isArchived === 1 ? 'mg-row--archived' : ''}`}
        style={{ transform: `translateX(${swipe.offset}px)` }}
        {...swipe.handlers}
      >
        <button
          className="mg-row-main"
          onClick={handleClick}
          aria-label={`Edit ${type.name}`}
        >
          <span
            className="mg-row-chip"
            style={{ background: type.colorHex, color: labelColorOn(type.colorHex) }}
            aria-hidden="true"
          >
            {type.shortcutText || type.name}
          </span>
          <span className="mg-row-name">{type.name}</span>
          {type.isArchived === 1 ? <span className="mg-row-tag">Archived</span> : null}
        </button>

        <button
          className={`mg-row-btn ${type.isPinned === 1 ? 'mg-row-btn--on' : ''}`}
          onClick={() => onTogglePin(type)}
          aria-label={type.isPinned === 1 ? `Unpin ${type.name}` : `Pin ${type.name}`}
          aria-pressed={type.isPinned === 1}
        >
          <span aria-hidden="true">★</span>
        </button>
        <button
          className="mg-row-btn"
          onClick={() => onMove(type.id, -1)}
          disabled={!canMoveUp}
          aria-label={`Move ${type.name} up`}
        >
          <span aria-hidden="true">↑</span>
        </button>
        <button
          className="mg-row-btn"
          onClick={() => onMove(type.id, 1)}
          disabled={!canMoveDown}
          aria-label={`Move ${type.name} down`}
        >
          <span aria-hidden="true">↓</span>
        </button>
      </div>
    </div>
  )
}
